import React from 'react';
import { useToggleTabsComponent } from '@/components/composite/ToggleTabs/ToggleTabs';
import { Loader } from '@/components/common/Loader';
import { useTranslations } from '@/core/translations';
import { AssetItemTypes } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types.db';
import { Colors } from '@/styles/colors';
import { useAssetGsiData } from '@/components/pages/SituationalAwareness/hooks/useAssetGsiData';
import { GsiScoreWidget } from '@/components/pages/SituationalAwareness/shared/GsiScoreWidget';
import { GsiTrendChart } from './components/GsiTrendChart/GsiTrendChart';
import { TargetAssetInfoProps, TargetAssetInfoView } from './TargetAssetInfoView';
import { TargetAssetDecisions } from './components/TargetAssetDecisions';
import { isGsiSupported } from '@/components/pages/SituationalAwareness/components/utils/incidentGsi';

import styles from './TargetAssetCard.module.scss';

interface TargetAssetCardProps {
  target: TargetAssetInfoProps['target'];
  actions?: TargetAssetInfoProps['actions'];
}

enum TargetAssetTab {
  INFO = 'info',
  DECISIONS = 'decisions',
}

export const TargetAssetCard: React.FC<TargetAssetCardProps> = ({ target, actions }) => {
  const t = useTranslations('incidents_page.assets_card');
  const isCorporateAsset = target.AssetType === AssetItemTypes.CORPORATE;
  const isGovernmentAsset = target.AssetType === AssetItemTypes.GOVERNMENT;

  const gsiCountry = isGovernmentAsset ? target.AssetName?.trim() || '' : '';
  const gsiSupported = isGsiSupported(gsiCountry);
  const { averageGsi, previousAverageGsi, gsiData, isLoading: isGsiLoading } = useAssetGsiData({
    location: gsiCountry,
    latitude: target.Latitude,
    longitude: target.Longitude,
    enabled: isGovernmentAsset,
  });

  const { ToggleTabs, viewTab } = useToggleTabsComponent<TargetAssetTab>({
    initialTab: TargetAssetTab.INFO,
    options: [
      { value: TargetAssetTab.INFO, children: t('info_title') },
      { value: TargetAssetTab.DECISIONS, children: t('decisions_title') },
    ],
  });

  if (isGsiLoading && isGovernmentAsset && gsiSupported) {
    return (
      <div className={styles.gsiLoader}>
        <Loader size="md" relative />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {isGovernmentAsset && gsiSupported && (
        <>
          {averageGsi != null &&(
            <GsiScoreWidget gsi={averageGsi} previousGsi={previousAverageGsi} />
          )}
          {gsiData && gsiData.length > 0 && <GsiTrendChart data={gsiData} />}
        </>
      )}
      {isCorporateAsset && (
        <ToggleTabs className={styles.tabs} tabClassName={styles.tabItem} buttonColor={Colors.Blue_10} />
      )}
      <div>
        {viewTab === TargetAssetTab.INFO && <TargetAssetInfoView target={target} actions={actions} />}
        {viewTab === TargetAssetTab.DECISIONS && <TargetAssetDecisions assetId={target.AssetID} />}
      </div>
    </div>
  );
};
